// @ts-nocheck
import { forwardRef, useCallback, useMemo } from 'react';
import { Autocomplete } from '@mui/material';

import { renderDefaultAutocompleteInput } from './components/RenderDefaultAutocompleteInput';
import { useAutofillSafeId } from './hooks';
import { mergeAutocompleteComponentsProps } from './utils/general';
import BaseFieldContainer from '../common/baseFieldContainer';

import type { KeyboardEvent } from 'react';
import type {
  FAutocompleteComponent,
  FAutocompleteDefaultTextFieldProps,
  FAutocompleteRenderFunction,
} from './types';
import type { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';

const FAutocompleteRender: FAutocompleteRenderFunction = (props, ref) => {
  const {
    label,
    helperText,
    error,
    required,
    disabled,
    fullWidth,
    InputLabelProps,
    placeholder,
    variant,
    size = 'small',
    sx,
    renderInput: userRenderInput,
    onKeyDown,
    stopKeyboardEventPropagation = false,
    componentsProps,
    requiredIndicatorSx,
    inputAutoComplete,
    inputAriaLabel,
    id,
    ...autocompleteRest
  } = props;

  const showRequiredIndicator = Boolean(required) && !disabled;

  const autofillSafeId = useAutofillSafeId(inputAutoComplete, id);

  const mergedComponentsProps = mergeAutocompleteComponentsProps(componentsProps);

  // Default TextField props for the Autocomplete component for performance optimization especially in heavy forms.
  const defaultTextFieldProps = useMemo<FAutocompleteDefaultTextFieldProps>(
    () => ({
      label,
      helperText,
      error,
      required,
      disabled,
      fullWidth,
      placeholder,
      InputLabelProps,
      variant,
      size,
      sx,
      hideLabelRequiredAsterisk: showRequiredIndicator,
      inputAutoComplete,
      inputAriaLabel,
    }),
    [
      label,
      helperText,
      error,
      required,
      disabled,
      fullWidth,
      placeholder,
      InputLabelProps,
      variant,
      size,
      sx,
      showRequiredIndicator,
      inputAutoComplete,
      inputAriaLabel,
    ],
  );

  const renderDefaultInput = useCallback(
    (params: AutocompleteRenderInputParams) =>
      renderDefaultAutocompleteInput(params, defaultTextFieldProps),
    [defaultTextFieldProps],
  );

  const renderInput = userRenderInput ?? renderDefaultInput;

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (stopKeyboardEventPropagation) {
        event.stopPropagation();
      }
      onKeyDown?.(event);
    },
    [onKeyDown, stopKeyboardEventPropagation],
  );

  return (
    <BaseFieldContainer
      required={required}
      disabled={disabled}
      helperText={helperText}
      error={error}
      fullWidth={fullWidth}
      requiredIndicatorSx={requiredIndicatorSx}
    >
      <Autocomplete
        ref={ref}
        id={id ?? autofillSafeId}
        disabled={disabled}
        fullWidth={fullWidth}
        size={size}
        renderInput={renderInput}
        onKeyDown={handleKeyDown}
        componentsProps={mergedComponentsProps}
        {...autocompleteRest}
      />
    </BaseFieldContainer>
  );
};

const FAutocomplete: FAutocompleteComponent = forwardRef(FAutocompleteRender);

export default FAutocomplete;
